import {
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type FirestoreError,
  type Query,
  type QuerySnapshot,
  type Unsubscribe,
} from '@firebase/firestore';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:snapshotRetry');

// @firebase/firestore 4.14.0's IndexedDB-backed persistentLocalCache
// retries a failed IndexedDB transaction 3 times internally, then — in
// eventManagerListen — calls the listener's error callback exactly ONCE and
// drops the listener for good. The nightly canary hits this intermittently
// right after a full page navigation: `code: 'unavailable'` with an
// "IndexedDB transaction '...' failed" message, and the screen then stays
// empty (structure page: "Unable to load property") until a reload. The SDK
// itself classes the error as recoverable (wrapInUserErrorIfRecoverable) and
// upstream's advice is to reload, so this resubscribes a few times with
// backoff; if every attempt fails, onError fires as before. Every other error
// (permission-denied, etc.) is passed straight through, unchanged, no retry.

const RETRY_DELAYS_MS = [500, 1500, 4000];

export function isRecoverableCacheError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const code = (err as { code?: unknown }).code;
  const message = (err as { message?: unknown }).message;
  return (
    code === 'unavailable' &&
    typeof message === 'string' &&
    message.includes('IndexedDB transaction')
  );
}

export function onSnapshotWithRetry<AppModelType, DbModelType extends DocumentData = DocumentData>(
  reference: DocumentReference<AppModelType, DbModelType>,
  onNext: (snapshot: DocumentSnapshot<AppModelType, DbModelType>) => void,
  onError?: (error: FirestoreError) => void
): Unsubscribe;
export function onSnapshotWithRetry<AppModelType, DbModelType extends DocumentData = DocumentData>(
  query: Query<AppModelType, DbModelType>,
  onNext: (snapshot: QuerySnapshot<AppModelType, DbModelType>) => void,
  onError?: (error: FirestoreError) => void
): Unsubscribe;
export function onSnapshotWithRetry(
  refOrQuery: DocumentReference<unknown, DocumentData> | Query<unknown, DocumentData>,
  onNext: (snapshot: any) => void,
  onError?: (error: FirestoreError) => void
): Unsubscribe {
  let attempt = 0;
  let stopped = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let innerUnsubscribe: Unsubscribe = () => {};

  const clearRetryTimer = () => {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const subscribe = () => {
    innerUnsubscribe = onSnapshot(
      refOrQuery as Query<unknown, DocumentData>,
      (snapshot) => {
        if (stopped) return;
        // A snapshot landed, so whatever was wrong with IndexedDB (if
        // anything) is resolved — start the next failure's backoff from 0.
        attempt = 0;
        onNext(snapshot);
      },
      (error) => {
        if (stopped) return;
        if (isRecoverableCacheError(error) && attempt < RETRY_DELAYS_MS.length) {
          const delay = RETRY_DELAYS_MS[attempt];
          attempt += 1;
          log.warn(
            `resubscribing after recoverable cache error (attempt ${attempt}/${RETRY_DELAYS_MS.length}, retrying in ${delay}ms):`,
            error.message
          );
          // The SDK already dropped this listener before calling us, but
          // unsubscribe defensively in case that behavior ever changes.
          innerUnsubscribe();
          clearRetryTimer();
          retryTimer = setTimeout(() => {
            retryTimer = null;
            if (!stopped) subscribe();
          }, delay);
          return;
        }
        onError?.(error);
      }
    );
  };

  subscribe();

  return () => {
    stopped = true;
    clearRetryTimer();
    innerUnsubscribe();
  };
}
